import {
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import {
  PANEL_GUTTER,
  PANEL_MIN_HEIGHT,
  PANEL_WIDTH,
  clampPanelPosition
} from "stores/wellCardPanel";
import type { PanelPosition } from "stores/wellCardPanel";
import {
  effectivePanelWidth,
  panelRoomInWindow
} from "stores/wellCardPanel/wellCardPanelStore";

/**
 * Which edge of the card the pointer went down on. There is no top: the card's
 * top edge is its placement, which the title bar already drags, and the height
 * follows it down.
 */
export type PanelEdge = "left" | "right" | "bottom";

export interface UsePanelResizeParams {
  /**
   * The card, read for the box it is already rendering at when a right-edge
   * drag has to place a card the CSS was holding against the window, and for
   * the top edge a bottom-edge drag measures its room from.
   */
  readonly panelRef: RefObject<HTMLElement>;
  /** The card's committed width, which is where each drag starts from. */
  readonly width: number;
  /**
   * The card's committed height, or null while the user has never set one -
   * where the card fills what is left below its top edge, and a drag starts
   * from that rather than from a number of its own.
   */
  readonly height: Maybe<number>;
  /** Where the card has been dragged to, or null while it is right-anchored. */
  readonly position: Maybe<PanelPosition>;
  /**
   * The furthest left the card may reach: the application's rail plus the gap
   * the design keeps from it. The same number the card's maximized left edge
   * uses, so a widened card stops exactly where a maximized one starts.
   */
  readonly leftBound: number;
  /** A maximized or docked card owns its geometry; resizing is off then. */
  readonly enabled: boolean;
  /**
   * Committed on release, not on every move. `position` is passed when the
   * drag moved the card's left edge, which is the one case where the width and
   * the placement have to be written together - and once mid-drag, when a
   * right-edge drag has to place a card before it can size it.
   */
  readonly onCommit: (width: number, position: Maybe<PanelPosition>) => void;
  /**
   * Committed on release, the same way the width is, and on its own rather
   * than folded in beside it: the bottom edge never moves the card, so a
   * height and a placement are never written together.
   */
  readonly onCommitHeight: (height: number) => void;
}

export interface PanelResize {
  /** The width under the pointer, while an edge is being dragged. */
  readonly liveWidth: Maybe<number>;
  /** The height under the pointer, while the bottom edge is being dragged. */
  readonly liveHeight: Maybe<number>;
  /** Where the card is while a left-edge drag walks its corner leftwards. */
  readonly livePosition: Maybe<PanelPosition>;
  readonly resizing: boolean;
  readonly onPointerDown: (edge: PanelEdge) => (event: ReactPointerEvent) => void;
}

interface LiveResize {
  readonly width: number;
  /**
   * Null on a side drag, so the card keeps whatever height it had. A number
   * here is what stops it filling to the foot of the window, and a horizontal
   * drag has no business doing that.
   */
  readonly height: Maybe<number>;
  readonly position: Maybe<PanelPosition>;
  /** The edge the pointer is on, which is the cursor the body takes. */
  readonly edge: PanelEdge;
}

/**
 * Drag either side edge of the card to set its width, or its bottom edge to
 * set its height.
 *
 * Each handle moves the edge it is on, and the other edge stays where it is.
 * How that is done depends on how the card is placed:
 *
 * - Placed by its top-left corner, which is where a card that has been dragged
 *   or widened from the right sits: the right handle leaves that corner alone,
 *   and the left handle walks it leftwards by exactly what the card gains.
 * - Untouched, the card is pinned to the right of the window by CSS, so its
 *   right edge cannot move at all and every pixel of a width change lands on
 *   the left one. That is what the left handle wants, so it is left as it is.
 *   The right handle cannot work like that - dragged inwards it would have
 *   walked the LEFT edge across the screen - so on the first move that changes
 *   the width it plants the card at the box it is already rendering, and sizes
 *   it from its own corner thereafter. Not on the press: a click that moves
 *   nothing leaves the card exactly as it was, and turning a card the window
 *   holds into one the user has placed is not nothing.
 *
 * The bottom edge is simpler than either of those, and deliberately so: it
 * moves no edge but its own. The card's top is where the card was put, and
 * placing it is the title bar's drag - so nothing here has to be planted, and
 * a height alone is what comes back.
 *
 * The size under the pointer is held here rather than in the store: it changes
 * with every move, and a store write per move would re-render every reader of
 * the card.
 */
export function usePanelResize({
  panelRef,
  width,
  height,
  position,
  leftBound,
  enabled,
  onCommit,
  onCommitHeight
}: UsePanelResizeParams): PanelResize {
  const [live, setLive] = useState<Maybe<LiveResize>>(null);

  // The body keeps the resize cursor for as long as the drag runs, so it does
  // not flick back to an arrow the moment the pointer outruns a 6px strip.
  // Restored here as well as on release, or an unmount mid-drag would leave
  // the whole application with a resize cursor.
  //
  // The cursor is the one the edge under way asks for, not one cursor for
  // every drag: an arrow lying across a drag that runs up and down points at
  // something the card will not do.
  const resizing = live != null;
  const cursor = live?.edge === "bottom" ? "ns-resize" : "ew-resize";
  useEffect(() => {
    if (!resizing) return;
    const body = document.body;
    body.style.cursor = cursor;
    body.style.userSelect = "none";
    return () => {
      body.style.removeProperty("cursor");
      body.style.removeProperty("user-select");
    };
  }, [resizing, cursor]);

  /**
   * Takes the running drag's listeners off the handle they were put on.
   *
   * Held here because only the drag's own end handler removes them, and the
   * handles are rendered behind `enabled`: maximize or dock the card with the
   * button still down and the element goes, so that handler never runs. What
   * would be left is the live width - the card keeps being drawn at it with
   * its transitions off, and the body keeps the resize cursor and the
   * selection lock that the effect above ties to it.
   */
  const detachDrag = useRef<Maybe<() => void>>(null);
  useEffect(() => {
    if (enabled) return;
    detachDrag.current?.();
    setLive(null);
  }, [enabled]);
  // On unmount the listeners go with the element, so there is only the
  // reference to them to drop - and no live width worth clearing, with nothing
  // left to draw it and the body's own styles restored by the effect above.
  useEffect(() => () => detachDrag.current?.(), []);

  const onPointerDown = useCallback(
    (edge: PanelEdge) => (event: ReactPointerEvent) => {
      if (!enabled || event.button !== 0) return;

      const startWidth = width;
      let startPosition = position;
      const startPointer = { x: event.clientX, y: event.clientY };
      // The card's own CSS caps the width at what the window leaves beside the
      // chrome; the drag stops there rather than letting the number run on
      // past a card that has stopped growing.
      const roomInWindow = panelRoomInWindow(leftBound);
      /*
       * What the window leaves below the card's top edge, which is the tallest
       * the card can be drawn - and so where a downward drag stops, for the
       * same reason the right handle stops at the window. The top edge does not
       * move while the bottom one is dragged, so it is measured once: from the
       * corner the card was placed at, or from the box the CSS is holding an
       * unplaced card in.
       *
       * The floor is in it as well, because a window shorter than the floor
       * draws the card hanging over the bottom edge rather than squeezed below
       * it, and the drag has to agree with what is on screen.
       */
      const roomBelowTop = (() => {
        const top = startPosition?.y ?? panelRef.current?.getBoundingClientRect().top;
        return Math.max(PANEL_MIN_HEIGHT, window.innerHeight - (top ?? 0) - PANEL_GUTTER);
      })();
      // An untouched card has no height of its own, so the drag starts from
      // the one it is drawn at - which is exactly the room below its top. The
      // bottom edge then follows the pointer from where the pointer found it,
      // rather than jumping to a number the card was never at.
      const startHeight = height ?? roomBelowTop;

      // A card still held against the right of the window cannot move its
      // right edge, so the handle the user has hold of would have moved the
      // other one. The corner it would be planted at is measured now, while
      // the card is still where the CSS put it, but nothing is written yet -
      // the press may turn out to be a click. Clamped the same way a move-drag
      // clamps - against the width the card is DRAWN at, which is the one the
      // box being measured here has - or the layout effect that watches the
      // position would correct this one a frame later and the card WOULD jump.
      const plantTo = (() => {
        if (startPosition || edge !== "right") return null;
        const box = panelRef.current?.getBoundingClientRect();
        return box
          ? clampPanelPosition(
              { x: box.left, y: box.top },
              effectivePanelWidth(startWidth, leftBound)
            )
          : null;
      })();

      const handle = event.currentTarget as HTMLElement;
      // Read now rather than off the event later: React hands the same pooled
      // event round, and its fields are cleared once this handler returns.
      const pointerId = event.pointerId;
      handle.setPointerCapture?.(pointerId);

      const widthAt = (pointer: PointerEvent, ceiling: number) => {
        const dx = pointer.clientX - startPointer.x;
        // Each handle grows the card when it is pulled away from it.
        const wanted = edge === "left" ? startWidth - dx : startWidth + dx;
        return Math.min(Math.max(wanted, PANEL_WIDTH), ceiling);
      };

      const heightAt = (pointer: PointerEvent) => {
        const wanted = startHeight + (pointer.clientY - startPointer.y);
        return Math.min(Math.max(wanted, PANEL_MIN_HEIGHT), roomBelowTop);
      };

      const resizeTo = (pointer: PointerEvent): LiveResize => {
        // The bottom handle changes the height and nothing else: the card's
        // top edge is its placement, so there is no corner to walk and nothing
        // to plant.
        if (edge === "bottom") {
          return {
            width: startWidth,
            height: heightAt(pointer),
            position: null,
            edge
          };
        }
        // Placed by its corner, and pulled by the left handle: the right edge
        // is the one that has to stay still, so it is measured once here and
        // the corner is worked back from the width.
        if (startPosition && edge === "left") {
          const rightEdge = startPosition.x + startWidth;
          const ceiling = Math.max(
            PANEL_WIDTH,
            Math.min(roomInWindow, rightEdge - leftBound)
          );
          const next = widthAt(pointer, ceiling);
          return {
            width: next,
            height: null,
            position: { ...startPosition, x: rightEdge - next },
            edge
          };
        }
        // Pulled by the right handle: the corner stays put, so what stops the
        // card is the window's right edge measured from that corner - not the
        // room a right-anchored card would have. Without it the number ran on
        // past the window while the card had stopped growing, and the clamp on
        // release then snapped the card leftwards. The corner is the one the
        // card is about to be planted at while it is still unplaced, so the
        // width the user sees is the width that is finally committed.
        const corner = startPosition ?? plantTo;
        const ceiling = corner
          ? Math.max(
              PANEL_WIDTH,
              Math.min(roomInWindow, window.innerWidth - corner.x - PANEL_GUTTER)
            )
          : roomInWindow;
        return {
          width: widthAt(pointer, ceiling),
          height: null,
          position: null,
          edge
        };
      };

      // The card is placed the moment the width first differs from the one it
      // started at, and never for a press that only jitters. Once only: the
      // corner it is planted at is the one the rest of the drag sizes from.
      const plant = () => {
        if (!plantTo || startPosition) return;
        startPosition = plantTo;
        onCommit(startWidth, plantTo);
      };

      const detach = () => {
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", end);
        handle.removeEventListener("pointercancel", end);
        detachDrag.current = null;
      };

      const move = (moveEvent: PointerEvent) => {
        // A second finger on the same strip is not this drag, and answering to
        // it would size the card from the first one's starting point.
        if (moveEvent.pointerId !== pointerId) return;
        const next = resizeTo(moveEvent);
        // The live width goes in first: it is what turns the card's
        // transitions off, so the render that places the card is never a
        // render that eases.
        setLive(next);
        if (next.width !== startWidth) plant();
      };

      const end = (endEvent: PointerEvent) => {
        // The other finger lifting does not end this drag.
        if (endEvent.pointerId !== pointerId) return;
        detach();
        const settled = resizeTo(endEvent);
        // Committed before the live width is given up, so the card cannot
        // render for a frame at the width it started at - with its transitions
        // back on, that frame is a visible slide back and out again.
        //
        // A press that moved nothing - a click on the strip, or a pull against
        // an edge already at its limit - is not a resize, and committing one
        // would write the card's own size back through the store for nothing.
        // Nor is it a placement: a click on the right handle leaves a card the
        // window is holding exactly where the window has it. The bottom edge
        // has its own case of this: a card that has never been sized starts
        // the drag at the height it fills to, so a press that ends there has
        // to leave it filling rather than pin it to that number.
        if (edge === "bottom") {
          if (settled.height !== startHeight) onCommitHeight(settled.height);
        } else if (settled.width !== startWidth) {
          plant();
          onCommit(settled.width, settled.position);
        }
        setLive(null);
      };

      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", end);
      handle.addEventListener("pointercancel", end);
      detachDrag.current = detach;
    },
    [enabled, panelRef, width, height, position, leftBound, onCommit, onCommitHeight]
  );

  return {
    liveWidth: live?.width ?? null,
    liveHeight: live?.height ?? null,
    livePosition: live?.position ?? null,
    resizing,
    onPointerDown
  };
}
